import React, { useCallback, useEffect, useState } from "react";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Pill, Plus, Edit2, Trash2, Search, X } from "lucide-react";
import { toast } from "sonner";
import RichTextEditor from "../components/RichTextEditor";
import { sanitizeHtml, htmlToPlainText } from "../lib/sanitize";

const empty = { name: "", description: "" };

export default function Medications() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [viewing, setViewing] = useState(null);
  const PAGE = 12;

  const load = useCallback((search = "", size = PAGE) => {
    api
      .get("/medications", { params: { ...(search ? { q: search } : {}), limit: size } })
      .then((r) => {
        setItems(r.data.items || []);
        setTotal(r.data.total || 0);
      })
      .catch((err) => console.error("Failed to load medications", err));
  }, []);
  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    const t = setTimeout(() => load(q), 250);
    return () => clearTimeout(t);
  }, [q, load]);

  const openNew = () => {
    setForm(empty);
    setEditingId(null);
    setOpen(true);
  };
  const openEdit = (m) => {
    setForm({ name: m.name || "", description: m.description || "" });
    setEditingId(m.id);
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/medications/${editingId}`, form);
        toast.success("Medication updated");
      } else {
        await api.post("/medications", form);
        toast.success("Medication added");
      }
      setOpen(false);
      load();
    } catch {
      toast.error("Failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this medication?")) return;
    await api.delete(`/medications/${id}`);
    toast.success("Deleted");
    load();
  };

  const stripHtml = (html = "") => htmlToPlainText(html);

  return (
    <div className="space-y-6 fade-up">
      <header className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-secondary font-bold">
            Library
          </p>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-1">
            Know Your Medications
          </h1>
          <p className="text-muted-foreground mt-2">
            Just a title and a free-form description. Write whatever you need.
          </p>
        </div>
        <Button onClick={openNew} data-testid="add-medication-btn" size="lg">
          <Plus className="size-4 mr-2" /> Add Medication
        </Button>
      </header>

      <div className="relative">
        <Search className="size-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search medications…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="pl-9 h-11"
          data-testid="medication-search"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((m) => (
          <Card key={m.id} className="p-5 flex flex-col" data-testid={`medication-card-${m.id}`}>
            <div className="flex items-start gap-3 mb-3">
              <div className="size-10 rounded-md bg-primary/10 text-primary grid place-items-center shrink-0">
                <Pill className="size-5" strokeWidth={1.6} />
              </div>
              <button
                type="button"
                className="text-left flex-1 min-w-0"
                onClick={() => setViewing(m)}
                data-testid={`view-medication-${m.id}`}
              >
                <p className="font-heading text-lg font-semibold truncate">{m.name}</p>
                {m.description && (
                  <p className="text-sm text-muted-foreground line-clamp-3 mt-1">
                    {stripHtml(m.description)}
                  </p>
                )}
              </button>
            </div>
            <div className="flex gap-2 mt-auto pt-3 border-t border-border">
              <Button
                variant="outline"
                size="sm"
                onClick={() => openEdit(m)}
                data-testid={`edit-medication-${m.id}`}
              >
                <Edit2 className="size-3.5 mr-1" /> Edit
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => remove(m.id)}
                className="text-destructive hover:text-destructive"
                data-testid={`delete-medication-${m.id}`}
              >
                <Trash2 className="size-3.5" />
              </Button>
            </div>
          </Card>
        ))}
        {items.length === 0 && (
          <Card className="col-span-full p-10 text-center border-dashed">
            <p className="text-muted-foreground">No medications yet.</p>
          </Card>
        )}
      </div>

      {items.length < total && (
        <div className="text-center">
          <Button
            variant="outline"
            onClick={() => load(q, items.length + PAGE)}
            data-testid="load-more-medications"
          >
            Load more ({total - items.length} remaining)
          </Button>
        </div>
      )}

      {/* Create / Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingId ? "Edit medication" : "New medication"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label>Title *</Label>
              <Input
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="med-form-name"
                placeholder="e.g. Metformin"
              />
            </div>
            <div>
              <Label>Description</Label>
              <RichTextEditor
                value={form.description}
                onChange={(html) => setForm({ ...form, description: html })}
                placeholder="Write uses, dosage, side effects, notes — anything you need."
                testIdPrefix="med-desc"
                minHeight="260px"
              />
            </div>
            <DialogFooter>
              <Button type="submit" data-testid="med-form-submit">
                {editingId ? "Update" : "Save"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Read-only view dialog */}
      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Pill className="size-5 text-primary" /> {viewing?.name}
            </DialogTitle>
          </DialogHeader>
          {viewing?.description ? (
            <div
              className="prose-content max-h-[60vh] overflow-auto"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(viewing.description) }}
            />
          ) : (
            <p className="text-muted-foreground">No description yet.</p>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                openEdit(viewing);
                setViewing(null);
              }}
              data-testid="view-edit-medication-btn"
            >
              <Edit2 className="size-4 mr-1" /> Edit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
